import React, { useEffect, useState, useContext, useMemo, useCallback, useRef } from "react";
import { Spinner, SpinnerSize } from "@fluentui/react/lib/Spinner";
import { Dropdown } from "@fluentui/react/lib/Dropdown";
import { t } from "i18next";
import { ACTIONS } from "../../../../constants/Constants";
import { listFirmHeaderTemplate } from "../../../../service/FirmServices";
import HeaderTemplateValue from "../../../Masters/HeaderTemplate/HeaderTemplateValue";
import userContext from "../../../../context/users/UserContext";
import toastContext from "../../../../context/toast/ToastContext";
import { TOAST_TYPE } from "../../../../constants/Constants";
import themeContext from "../../../../context/theme/ThemeContext";

const HeaderReportTemplateTab = ({ 
  currFirmId, 
  action, 
  roles,
  onTemplateSelect,
  initialTemplateId 
}) => {
  const { firmId } = useContext(userContext);
  const { showToastv2 } = useContext(toastContext);
  const { getThemeColor } = useContext(themeContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [templateList, setTemplateList] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  
  const isInitializedRef = useRef(false);
  const isUpdatingFromParentRef = useRef(false);
  const previousInitialIdRef = useRef(null);
  const savedTemplateRef = useRef(null);

  const resolvedFirmId = useMemo(() => {
    return currFirmId != null && currFirmId !== "" ? currFirmId : firmId;
  }, [currFirmId, firmId]);

  useEffect(() => {
    if (resolvedFirmId) {
      fetchTemplates();
    }
  }, [resolvedFirmId]);

  useEffect(() => {
    if (
      templateList.length > 0 && 
      initialTemplateId != null && 
      initialTemplateId !== previousInitialIdRef.current &&
      !isUpdatingFromParentRef.current
    ) {
      previousInitialIdRef.current = initialTemplateId;
      setSelectedTemplateId(initialTemplateId);
      
      const found = templateList.find((t) => t.id === initialTemplateId);
      setPreviewTemplate(found || null);
      savedTemplateRef.current = found;
      
    }
  }, [initialTemplateId, templateList]);
        useEffect(() => {
        // Skip if not initialized yet or if templates aren't loaded
        if (!isInitializedRef.current || templateList.length === 0) {
            return;
        }

        if (selectedTemplateId != null) {
            const found = templateList.find((t) => t.id === selectedTemplateId);
            setPreviewTemplate(found || null);
        }
        }, [selectedTemplateId, templateList]);

  const fetchTemplates = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await listFirmHeaderTemplate(resolvedFirmId);
      if (response?.status === 200 && response?.data?.result === "SUCCESS") {
        const data = response.data.data;
        const list = data.masterStudyReportList || [];
        const currentId = initialTemplateId || data.masterStudyReportId;

        setTemplateList(list);
        setSelectedTemplateId(currentId);
        previousInitialIdRef.current = currentId;

        const current = list.find((item) => item.id === currentId);
        setPreviewTemplate(current || list[0] || null);
        savedTemplateRef.current = current || list[0] || null;
        
        // Mark as initialized after setting initial values
        isInitializedRef.current = true;
        
        // Initial notification to parent (only if different from current parent value)
        if (onTemplateSelect && currentId && currentId !== initialTemplateId) {
          isUpdatingFromParentRef.current = true;
          onTemplateSelect(currentId);
          setTimeout(() => {
            isUpdatingFromParentRef.current = false;
          }, 100);
        }
      } else {
        setError(response?.data?.message || t("string-literals.error-msg"));
        showToastv2(TOAST_TYPE.ERROR, response?.data?.message || t("string-literals.error-msg"));
      }
    } catch (err) {
      setError(t("string-literals.error-msg"));
      showToastv2(TOAST_TYPE.ERROR, t("string-literals.error-msg"));
    } finally {
      setLoading(false);
    }
  };

  const handleDropdownChange = useCallback((event, option) => {
  if (option && option.key) {
    const id = parseInt(option.key, 10);
    if (id !== selectedTemplateId) {
      setSelectedTemplateId(id);

      if (onTemplateSelect) {
         isUpdatingFromParentRef.current = true;
        onTemplateSelect(id);
      }
    }
  }
}, [selectedTemplateId, onTemplateSelect]);


  const dropdownOptions = useMemo(() => {
    return templateList.map((template) => ({
      key: template.id.toString(),
      text: template.title || template.name || `${t("hospital.template") || "Template"} ${template.id}`,
      data: template
    }));
  }, [templateList, t]);


  if (error) {
    return (
      <div style={styles.centered}>
        <p style={styles.errorText}>{error}</p>
      </div>
    );
  }

  if (templateList.length === 0) {
    return (
      <div style={styles.centered}>
        <p style={styles.emptyText}>
          {t("hospital.no-header-templates") || "No header templates available."}
        </p>
      </div>
    );
  }

  const isEdit = action === ACTIONS.EDIT;
  const selectedKey = selectedTemplateId?.toString();

  return (
    <div style={styles.container}>
      <div style={styles.sectionHeader}>
        <p style={styles.sectionTitle}>
          {t("hospital.header-report-template") || "Header Report Template"}
          {savedTemplateRef.current && (
            <span style={styles.selectedTemplateBadge}>
              {savedTemplateRef.current.title || savedTemplateRef.current.name || `${t("hospital.template") || "Template"} ${savedTemplateRef.current.id}`}
            </span>
          )}
        </p>
      {isEdit && (
  <div style={styles.dropdownWrapper}>
    <span style={styles.dropdownLabel}>
      {t("hospital.select-template") || "Select Template"}:
    </span>
    <Dropdown
      options={dropdownOptions}
      selectedKey={selectedKey}
      onChange={handleDropdownChange}
      placeholder={t("hospital.select-template-placeholder") || "Select a template"}
      styles={{
        root: { width: "140px" },
        title: { height: "32px", lineHeight: "32px" },
        dropdown: { height: "32px" }
      }}
    />
  </div>
)}
      </div>


      {previewTemplate ? (
        <div style={styles.previewWrapper}>
          <div style={styles.previewHeader}>
          </div>
          {/* <div style={isEdit ? styles.previewBox : styles.previewBoxReadOnly}> */}
          <div
              style={{
                ...(isEdit ? styles.previewBox : styles.previewBoxReadOnly),
                backgroundColor: isEdit
                  ? getThemeColor("report-template-container")
                  : getThemeColor("report-template-container"),

                border: getThemeColor("border"),
              }}
            >
            <HeaderTemplateValue
              report={previewTemplate.report?.report || previewTemplate.report}
              id={previewTemplate.id}
            />
          </div>
        </div>
      ) : (
        <div style={styles.centered}>
          <p style={styles.emptyText}>
            {t("hospital.no-template-selected") || "No template selected."}
          </p>
        </div>
      )}
    </div>
  );
};


const styles = {
  container: {
    margin: "24px 40px",
    fontFamily: "Open Sans, sans-serif",
  },
  sectionHeader: {
    display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "20px",
  },
  sectionTitle: {
  fontSize: "18px",
  fontWeight: "600",
  margin: "0",
  display: "flex",
  alignItems: "center",
},
  sectionSubtitle: {
    fontSize: "13px",
    // color: "#605E5C",
    margin: 0,
  },
  dropdownWrapper: {
  marginLeft: "16px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
},
dropdownLabel: {
  fontSize: "14px",
  fontWeight: "400",
  // color: "#323130",
  whiteSpace: "nowrap",
},
  previewWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  previewHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  previewLabel: {
    fontSize: "13px",
    fontWeight: "600",
    // color: "#323130",
    margin: 0,
  },
  previewBox: {
     border: "1px solid #EDEBE9",
    borderRadius: "4px",
     backgroundColor: "#F2ECEC",
    padding: "10px",
    minHeight: "300px",
    maxHeight: "600px",
    overflowY: "auto",
  },
  previewBoxReadOnly: {
     border: "1px solid #EDEBE9",
    borderRadius: "4px",
     backgroundColor: "#FAF9F8",
    padding: "10px",
    minHeight: "300px",
    maxHeight: "600px",
    overflowY: "auto",
  },
  centered: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
  errorText: {
    color: "#A4262C",
    fontSize: "14px",
  },
  emptyText: {
    color: "#605E5C",
    fontSize: "14px",
  },
  selectedTemplateBadge: {
  marginLeft: "8px",
  fontSize: "12px",
  padding: "4px 12px",
  backgroundColor: "#E1DFDD",
  color: "#323130",
  borderRadius: "16px",
  fontWeight: "500",
},
};

export default HeaderReportTemplateTab;